/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, {
    IComponent,
    IComponentProps,
    ICoreContext,
    IDateFormatOptions,
    isOboRequest,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { getInvoiceDetailsPageUrlSync, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button, format } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React, { useState } from 'react';

import { InvoiceModel } from '../../../helpers/invoice-data';
import { getInvoiceStatusText, IInvoiceStatusResources } from '../../../helpers/invoice-status';
import { IInvoiceErrorState } from '../../../invoices-list';
import { IPayInvoiceResources, PayInvoiceComponent } from '../../pay-invoice-component';
import { RequestInvoiceButton } from '../../request-invoice-button';

interface IInvoicesTableRowData {
    invoice: InvoiceModel;
    index: number;
    customer: Customer;
}

export interface IInvoicesTableRowResources extends IInvoiceStatusResources, IPayInvoiceResources {
    invoiceCurrentUser: string;
    invoiceViewDetailsButtonText: string;
    invoiceViewDetailsButtonAriaLabel: string;
    invoiceActionsButtonTextAriaLabel?: string;
    invoiceActionsButtonIsOnBehalfOfTextAriaLabel?: string;
    invoiceRequestButtonLabel: string;
    invoiceListSelectRadioAriaLabelText: string;
}

export interface IInvoicesTableRowProps extends IComponentProps<IInvoicesTableRowData> {
    context: ICoreContext;
    className: string;
    isMobile: boolean;
    invoiceReferences?: React.RefObject<HTMLButtonElement>;
    payInvoiceReferences?: React.RefObject<HTMLButtonElement>;
    resources: IInvoicesTableRowResources;

    onChange(): void;
    onError?(errorState: IInvoiceErrorState, reference?: React.RefObject<HTMLButtonElement>): void;
    onRequestInvoice?(invoice: InvoiceModel, requestInvoiceButtonReference: React.RefObject<HTMLButtonElement>): void;
}

export interface IInvoicesTableRowComponent extends IComponent<IInvoicesTableRowProps> {}

const formatAmount = (context: ICoreContext, amount: number | undefined, currencyCode: string | undefined) => {
    if (amount === undefined) {
        return '';
    }
    return context.cultureFormatter.formatCurrency(amount, currencyCode);
};

const formatDate = (context: ICoreContext, date: Date | undefined) => {
    if (date === undefined) {
        return '';
    }

    const dateOptions: IDateFormatOptions = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' };
    return context.cultureFormatter.formatDate(date, dateOptions);
};

/**
 * Request specific invoice function.
 * @param props -Invoice table row props.
 * @param requestInvoiceButtonReference -Reference button.
 * @returns Call Request Invoice.
 */
const onRequestSpecificInvoice = (
    props: IInvoicesTableRowProps,
    requestInvoiceButtonReference: React.RefObject<HTMLButtonElement>,
    setShowActions: React.Dispatch<React.SetStateAction<boolean>>
) => () => {
    setShowActions(false);
    props.onRequestInvoice!(props.data.invoice, requestInvoiceButtonReference);
};

const getDesktopRowCells = (props: IInvoicesTableRowProps, onSelectLine: () => void) => {
    const {
        data: { invoice, customer }
    } = props;

    const isCurrentUser =
        !StringExtensions.isNullOrWhitespace(customer.AccountNumber) && customer.AccountNumber === invoice.customerAccountNumber;

    const linkToInvoiceDetails = getInvoiceDetailsPageUrlSync(props.data.invoice.id, props.context.actionContext);
    const invoiceRowArialabel = format(props.resources.invoiceListSelectRadioAriaLabelText, invoice.id);
    return (
        <>
            <td>
                <label className={classnames(`${props.className}__checkbox-container`, 'checkbox-container')}>
                    <input
                        className={classnames(`${props.className}__checkbox-input`, 'checkbox-input')}
                        type='checkbox'
                        checked={invoice.isSelected}
                        aria-label={invoiceRowArialabel}
                        aria-checked={invoice.isSelected}
                        onChange={onSelectLine}
                    />
                    <span className={classnames(`${props.className}__checkmark`, 'checkmark')} />
                </label>
            </td>
            <td>
                <a className={`${props.className}__open-invoice`} href={linkToInvoiceDetails}>
                    {invoice.id}
                </a>
            </td>
            <td>
                <b>{formatDate(props.context, invoice.invoiceDate)}</b>
            </td>
            <td>
                {invoice.customerName}
                {isCurrentUser ? <b>{props.resources.invoiceCurrentUser}</b> : null}
            </td>
            <td>{formatAmount(props.context, invoice.amount, invoice.currencyCode)}</td>
            <td>
                <b>{formatDate(props.context, invoice.dueDate)}</b>
            </td>
            <td>{formatAmount(props.context, invoice.amountDue, invoice.currencyCode)}</td>
            <td>
                <b>{getInvoiceStatusText(props.resources, invoice.status)}</b>
            </td>
            <td>{extraActionsPopup(props)}</td>
        </>
    );
};

let setShowPreviousActions: React.Dispatch<React.SetStateAction<boolean>> | undefined;

/**
 * On Click view details function.
 * @param linkToInvoiceDetails -Link to invoice details.
 * @returns Go to invoice details.
 */
const onClickViewDetails = (linkToInvoiceDetails: string) => () => {
    if (MsDyn365.isBrowser) {
        window.location.href = linkToInvoiceDetails;
    }
    return;
};

/**
 * Toggle function.
 * @param isShowActions -Link to invoice details.
 * @param setShowActions -Link to invoice details.
 * @returns Go to invoice details.
 */
const toggle = (isShowActions: boolean, setShowActions: React.Dispatch<React.SetStateAction<boolean>>) => () => {
    const shouldShowActions = !isShowActions;
    if (shouldShowActions) {
        if (setShowPreviousActions) {
            setShowPreviousActions(false);
        }
        setShowActions(shouldShowActions);
        setShowPreviousActions = setShowActions;
    } else {
        setShowPreviousActions = undefined;
        setShowActions(shouldShowActions);
    }
};

function useOutsideClick(refinerRefernce: React.RefObject<HTMLElement>, toggle: (event: Event | MouseEvent) => void) {
    function handleClickOutside(event: Event | MouseEvent) {
        if (refinerRefernce.current && !refinerRefernce.current.contains(event.target as Node)) {
            toggle(event);
        }
    }

    React.useEffect(() => {
        if (MsDyn365.isBrowser) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            if (MsDyn365.isBrowser) {
                document.removeEventListener('mousedown', handleClickOutside);
            }
        };
    }, [refinerRefernce, toggle]);
}

const extraActionsPopup = (props: IInvoicesTableRowProps): JSX.Element | null => {
    const {
        resources: {
            invoiceViewDetailsButtonAriaLabel,
            invoiceViewDetailsButtonText,
            invoiceActionsButtonTextAriaLabel,
            invoiceActionsButtonIsOnBehalfOfTextAriaLabel
        },
        data: { index }
    } = props;

    const [showActions, setShowActions] = useState(false);
    const isOnBehalfOfRequest = isOboRequest(props.context.request);

    const linkToInvoiceDetails = getInvoiceDetailsPageUrlSync(props.data.invoice.id, props.context.actionContext);

    const className = `${props.className}__extra-actions-cell`;
    const actionsContainerClassName = `${className}__actions-container`;
    const payInvoiceClassName = `${actionsContainerClassName}__pay-invoice`;
    const wrapperRefiner = React.useRef(null);
    useOutsideClick(wrapperRefiner, toggle(showActions && !isOnBehalfOfRequest, setShowActions));
    return (
        <div className={className}>
            <Button
                className={`${className}__toggle`}
                aria-label={isOnBehalfOfRequest ? invoiceActionsButtonIsOnBehalfOfTextAriaLabel : invoiceActionsButtonTextAriaLabel}
                onClick={toggle(showActions && !isOnBehalfOfRequest, setShowActions)}
                disabled={isOnBehalfOfRequest}
            />
            {showActions && !isOnBehalfOfRequest && (
                <div className={actionsContainerClassName} ref={wrapperRefiner}>
                    <Button
                        className={`${actionsContainerClassName}__view-details`}
                        onClick={onClickViewDetails(linkToInvoiceDetails)}
                        aria-label={invoiceViewDetailsButtonAriaLabel}
                    >
                        {invoiceViewDetailsButtonText}
                    </Button>
                    <PayInvoiceComponent
                        id={`${payInvoiceClassName}-${index}`}
                        className={payInvoiceClassName}
                        typeName={props.typeName}
                        context={props.context}
                        resources={props.resources}
                        data={{ invoice: props.data.invoice }}
                        onError={props.onError}
                        reference={props.payInvoiceReferences}
                    />
                    <RequestInvoiceButton
                        innerReference={props.invoiceReferences}
                        className={actionsContainerClassName}
                        requestInvoiceButtonLabel={props.resources.invoiceRequestButtonLabel}
                        onClicked={onRequestSpecificInvoice(props, props.invoiceReferences!, setShowActions)}
                    />
                </div>
            )}
        </div>
    );
};

const getMobileRowCells = (props: IInvoicesTableRowProps, onSelectLine: () => void) => {
    const {
        data: { invoice }
    } = props;

    const linkToInvoiceDetails = getInvoiceDetailsPageUrlSync(props.data.invoice.id, props.context.actionContext);
    const invoiceRowArialabel = format(props.resources.invoiceListSelectRadioAriaLabelText, invoice.id);

    return (
        <>
            <td>
                <label className={classnames(`${props.className}__checkbox-container`, 'checkbox-container')}>
                    <input
                        className={classnames(`${props.className}__checkbox-input`, 'checkbox-input')}
                        type='checkbox'
                        checked={invoice.isSelected}
                        aria-label={invoiceRowArialabel}
                        aria-checked={invoice.isSelected}
                        onChange={onSelectLine}
                    />
                    <span className={classnames(`${props.className}__checkmark`, 'checkmark')} />
                </label>
            </td>
            <td>
                <a className={`${props.className}__open-invoice`} href={linkToInvoiceDetails}>
                    {invoice.id}
                </a>
                <br />
                <b>{invoice.customerName}</b>
            </td>
            <td>
                <b>{formatDate(props.context, invoice.dueDate)}</b>
                <br />
                <span>{getInvoiceStatusText(props.resources, invoice.status)}</span>
            </td>
            <td>{extraActionsPopup(props)}</td>
        </>
    );
};

const InvoicesTableRow: React.FC<IInvoicesTableRowProps> = (props: IInvoicesTableRowProps): JSX.Element => {
    const {
        data: { invoice }
    } = props;

    const onSelectLine = () => {
        invoice.isSelected = !invoice.isSelected;
        props.onChange();
    };

    let rowCells: JSX.Element;
    if (props.isMobile) {
        rowCells = getMobileRowCells(props, onSelectLine);
    } else {
        rowCells = getDesktopRowCells(props, onSelectLine);
    }

    return <tr className={props.className}>{rowCells}</tr>;
};

export const InvoicesTableRowComponent: React.FunctionComponent<IInvoicesTableRowProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error
    IInvoicesTableRowComponent
>('InvoicesTableRowComponent', { component: InvoicesTableRow });
